// submitChatMessage.js
'use strict';
import parseLinkHeader from 'parse-link-header';

import fetch from '../resource/customFetch.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import trackEvent, { MESSAGE_SENT_FAILED } from '../resource/trackEvent.js';
import { Status as UploadStatus } from '../resource/uploadConstants.js';

import login from '../action/login.js';
import pushToastr from '../action/pushToastr.js';
import subscribePresenceMessage from '../action/subscribePresenceMessage.js';
import submitUploadAsset from '../action/submitUploadAsset.js';
import setUploadJobStatus from '../action/setUploadJobStatus.js';
import changeAssetId from '../action/changeAssetId.js';

import getMeData from '../selector/getMeData.js';
import getMessageData from '../selector/getMessageData.js';
import getAssetData from '../selector/getAssetData.js';

import {
  MERGE_ASSET_DATA,
  MERGE_OPERATION_DATA,
  CHANGE_MESSAGE_ID,
  ADD_LIST_ITEMS,
  REMOVE_LIST_ITEMS,
  CLEAR_NETWORKING_NODES,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

/**
 * @typedef {Object} Media
 * @property {string} name
 * @property {string} type Media type
 * @property {Blob} fileBlob - message Media Blob.
 */

/**
 * Send chat message
 * @kind action
 * @param {string} { text } - message text.
 * @param {string} { chatroomId } - chatroom id.
 * @param {string} { replyMessageId } - be replied message id.
 * @param {array} { assetIds } - message media asset ids.
 * @param {string} { localMessageId } - local generated message id.
 * @param {string} { uploadJobId } - upload job id.
 * @return {Promise} Action promise.
 */
const submitChatMessage =
  ({
    text,
    chatroomId,
    replyMessageId,
    assetIds = [],
    localMessageId,
    uploadJobId,
  }) =>
  async (dispatch, getState) => {
    const token = getMeData(getState(), 'token');

    let messageId = localMessageId;

    if (!token) {
      return dispatch(login({ shouldChooseEntryOption: false }));
    }

    const isReply = !!replyMessageId;

    const headers = new Headers({
      ...getHeaders(),
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    const body = {
      unlockPrice: 0,
    };

    if (text) {
      body.text = text;
    }

    if (replyMessageId) {
      body.replyOf = replyMessageId;
    }

    if (assetIds.length) {
      body.assets = assetIds.map(assetId => ({
        'content-type': getAssetData(getState(), [assetId], 'contentType'),
        'content-length': getAssetData(getState(), [assetId], 'contentLength'),
        'content-md5': getAssetData(getState(), [assetId], 'contentMd5'),
      }));
    }

    const fetchOptions = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    };

    const url = getResourceUrl({
      endpoint: `/chat/${chatroomId}/messages`,
    });

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: {
        selectPath: ['chatroom', chatroomId, 'sendChatMessage', messageId],
      },
    });

    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      let payload = {};

      try {
        payload = (await response.json()) || {};
        // eslint-disable-next-line no-empty
      } catch (_) {}

      messageId = payload.id;

      dispatch(subscribePresenceMessage({ messageId }));
      dispatch(
        replaceLocalMessage({
          chatroomId,
          messageId,
          localMessageId,
          uploadJobId,
        })
      );

      const pendingAssets = payload['pending_assets'] || [];

      if (pendingAssets.length) {
        for (let index = 0; index < pendingAssets.length; index++) {
          const localAssetId = assetIds[index];
          const pendingAsset = pendingAssets[index];
          const realAssetId = pendingAsset.id;
          const uploadUrl = pendingAsset['uploadUrl'];
          const tencentUploadUrl = pendingAsset['tencent_upload_url'];
          const isUploadCompleted = uploadUrl == null;
          dispatch(
            changeAssetId({
              oldId: localAssetId,
              newId: realAssetId,
              uploadJobId,
            })
          );
          dispatch({
            type: MERGE_ASSET_DATA,
            payload: {
              selectPath: [realAssetId],
              data: isUploadCompleted
                ? {
                    status: UploadStatus.UPLOAD_COMPLETED,
                    progress: 100,
                  }
                : {
                    uploadUrl,
                    tencentUploadUrl,
                  },
            },
          });
          if (!isUploadCompleted) {
            await dispatch(submitUploadAsset({ assetId: realAssetId }));
          }
        }
        dispatch(
          setUploadJobStatus({
            uploadJobId,
            status: UploadStatus.UPLOAD_COMPLETED,
          })
        );
      }

      /**
       * @deprecated Backend not return 202 anymore, can remove this block.
       */
      if (202 === response.status) {
        const responseLink = parseLinkHeader(response.headers.get('link'));
        const resumableUrl = responseLink['upload-resumable'].url;
        const assetId = assetIds[0];
        dispatch({
          type: MERGE_ASSET_DATA,
          payload: {
            selectPath: [assetId],
            data: {
              uploadUrl: resumableUrl,
            },
          },
        });
        await dispatch(submitUploadAsset({ assetId }));
      }
      dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: {
          selectPath: ['chatroom', chatroomId, 'sendChatMessage', messageId],
        },
      });
    } catch (error) {
      let errorTextKey = 'send_chat_error';
      try {
        const code = JSON.parse(error.message).code;
        if (code === 'UserBlocked') {
          errorTextKey = 'something_went_wrong_block';
        } else if (code) {
          errorTextKey = code;
        }
        // eslint-disable-next-line no-empty
      } catch (_) {}

      if (isReply) {
        trackEvent({
          type: MESSAGE_SENT_FAILED,
          payload: {
            reason: error.message || error.stack,
            type: 'reply',
            id: replyMessageId,
            mediaType: getMessageData(getState(), replyMessageId, 'mediaType'),
          },
        });
      } else {
        trackEvent({
          type: MESSAGE_SENT_FAILED,
          payload: {
            reason: errorTextKey,
            type: 'chat',
            id: null,
            mediaType: null,
          },
        });
      }
      dispatch(
        pushToastr({
          textKey: `error_${errorTextKey.toLowerCase()}`,
          color: 'error',
        })
      );
      if (uploadJobId) {
        setUploadJobStatus({
          uploadJobId,
          status: UploadStatus.UPLOAD_FAILED,
          failedReason: errorTextKey,
        });
      }
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: {
          selectPath: ['chatroom', chatroomId, 'sendChatMessage', messageId],
          error,
        },
      });
    }
  };

/**
 * Replace local message with real message id.
 * @kind action
 * @param {string} {chatroomId} - chatroom id.
 * @param {string} {messageId} - message id.
 * @param {string} {localMessageId} - local generated message id.
 * @return {Promise} Action promise.
 */
const replaceLocalMessage =
  ({ chatroomId, messageId, localMessageId, uploadJobId }) =>
  async dispatch => {
    const pendingListPath = ['chatroom', chatroomId, 'pendingMessages'];
    dispatch({
      type: CLEAR_NETWORKING_NODES,
      payload: {
        selectPaths: [
          ['chatroom', chatroomId, 'sendChatMessage', localMessageId],
        ],
      },
    });
    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: {
        selectPath: ['chatroom', chatroomId, 'sendChatMessage', messageId],
      },
    });
    dispatch({
      type: CHANGE_MESSAGE_ID,
      payload: {
        oldId: localMessageId,
        newId: messageId,
      },
    });
    dispatch({
      type: ADD_LIST_ITEMS,
      payload: {
        selectPath: pendingListPath,
        itemIds: [messageId],
      },
    });
    dispatch({
      type: REMOVE_LIST_ITEMS,
      payload: {
        selectPath: pendingListPath,
        itemIds: [localMessageId],
      },
    });
    if (uploadJobId) {
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['uploadJob', uploadJobId],
          data: {
            messageId,
          },
        },
      });
    }
  };

export default submitChatMessage;
